"use client";

import Image from "next/image";
import { TextAnimate } from "./TextAnimation";
import { motion } from "motion/react";
import Link from "next/link";
import NavbarGradientButton from "./NavbarGradientButton";
import type { HeroSectionData } from "@/types/cms";
import { openCalModal } from "./PageWithCalModal";
import { useCallback } from "react";
import { trackCTAClicked, trackMeetingModalOpened } from "@/lib/analytics";
import ProfilePersonaContainer, {
  ProfileDetail,
} from "./ProfilePersonaContainer";

interface LandingPageComponentProps {
  data?: HeroSectionData;
}

// Sample profile data for container
const sampleProfiles: ProfileDetail[] = [
  {
    user: {
      first_name: "Sarah",
      last_name: "Mitchell",
      profile_picture_url: "/guys/woman/w-1.webp",
    },
    headline: "CEO & Founder",
    company: {
      name: "Solisium Labs",
      logo_url: "/companies/Solisium Labs.webp",
      role: "Chief Executive Officer",
      location: "San Francisco, CA",
      since_date: "March 2019 - Present",
      description:
        "Leading innovation in quantum computing and AI-powered solutions for enterprise customers.",
      size: "200-500",
      valuation: "$150M",
      growth: "220%",
    },
    attributes: [
      {
        key: "location",
        title: "Location",
        value: "San Francisco, California",
      },
      {
        key: "experience",
        title: "Work Experience",
        value: "15+ years in tech leadership",
      },
    ],
  },
  {
    user: {
      first_name: "Michael",
      last_name: "Anderson",
      profile_picture_url: "/guys/man/m-1.webp",
    },
    headline: "CTO",
    company: {
      name: "Meridian Forge",
      logo_url: "/companies/Meridian Forge.webp",
      role: "Chief Technology Officer",
      location: "Austin, TX",
      since_date: "June 2020 - Present",
      description:
        "Building next-generation cloud infrastructure and distributed systems for global scale.",
      size: "150-400",
      valuation: "$120M",
      growth: "185%",
    },
    attributes: [
      { key: "location", title: "Location", value: "Austin, Texas" },
      {
        key: "experience",
        title: "Work Experience",
        value: "18+ years in software engineering",
      },
    ],
  },
  {
    user: {
      first_name: "Emily",
      last_name: "Chen",
      profile_picture_url: "/guys/woman/w-2.webp",
    },
    headline: "VP of Product",
    company: {
      name: "BlueCove Dynamics",
      logo_url: "/companies/BlueCove Dynamics.webp",
      role: "Vice President of Product",
      location: "Seattle, WA",
      since_date: "January 2021 - Present",
      description:
        "Creating innovative product experiences that connect millions of users worldwide.",
      size: "100-300",
      valuation: "$95M",
      growth: "160%",
    },
    attributes: [
      { key: "location", title: "Location", value: "Seattle, Washington" },
      {
        key: "experience",
        title: "Work Experience",
        value: "12+ years in product management",
      },
    ],
  },
  {
    user: {
      first_name: "David",
      last_name: "Rodriguez",
      profile_picture_url: "/guys/man/m-2.webp",
    },
    headline: "Head of Engineering",
    company: {
      name: "Arcadia Nexus",
      logo_url: "/companies/Arcadia Nexus.webp",
      role: "Head of Engineering",
      location: "Boston, MA",
      since_date: "September 2018 - Present",
      description:
        "Pioneering advanced analytics and machine learning solutions for data-driven enterprises.",
      size: "180-450",
      valuation: "$130M",
      growth: "195%",
    },
    attributes: [
      { key: "location", title: "Location", value: "Boston, Massachusetts" },
      {
        key: "experience",
        title: "Work Experience",
        value: "16+ years in engineering leadership",
      },
    ],
  },
  {
    user: {
      first_name: "Jessica",
      last_name: "Taylor",
      profile_picture_url: "/guys/woman/w-3.webp",
    },
    headline: "Chief Marketing Officer",
    company: {
      name: "VertoWave Systems",
      logo_url: "/companies/VertoWave Systems.webp",
      role: "CMO",
      location: "New York, NY",
      since_date: "April 2020 - Present",
      description:
        "Transforming brand strategies through digital innovation and customer-centric approaches.",
      size: "120-350",
      valuation: "$110M",
      growth: "175%",
    },
    attributes: [
      { key: "location", title: "Location", value: "New York, New York" },
      {
        key: "experience",
        title: "Work Experience",
        value: "14+ years in marketing",
      },
    ],
  },
  {
    user: {
      first_name: "James",
      last_name: "Park",
      profile_picture_url: "/guys/man/m-3.webp",
    },
    headline: "Director of Operations",
    company: {
      name: "Emberfield Collective",
      logo_url: "/companies/Emberfield Collective.webp",
      role: "Director of Operations",
      location: "Chicago, IL",
      since_date: "July 2019 - Present",
      description:
        "Optimizing operational excellence through automation and strategic process innovation.",
      size: "90-250",
      valuation: "$85M",
      growth: "150%",
    },
    attributes: [
      { key: "location", title: "Location", value: "Chicago, Illinois" },
      {
        key: "experience",
        title: "Work Experience",
        value: "13+ years in operations",
      },
    ],
  },
  {
    user: {
      first_name: "Rachel",
      last_name: "Williams",
      profile_picture_url: "/guys/woman/w-4.webp",
    },
    headline: "VP of Sales",
    company: {
      name: "Novara Ventures",
      logo_url: "/companies/Novara Ventures.webp",
      role: "Vice President of Sales",
      location: "Miami, FL",
      since_date: "February 2021 - Present",
      description:
        "Driving exponential revenue growth through innovative sales strategies and partnerships.",
      size: "70-200",
      valuation: "$75M",
      growth: "200%",
    },
    attributes: [
      { key: "location", title: "Location", value: "Miami, Florida" },
      {
        key: "experience",
        title: "Work Experience",
        value: "11+ years in sales leadership",
      },
    ],
  },
  {
    user: {
      first_name: "Alex",
      last_name: "Thompson",
      profile_picture_url: "/guys/man/m-4.webp",
    },
    headline: "Lead Data Scientist",
    company: {
      name: "Helixa Solutions",
      logo_url: "/companies/Helixa Solutions.webp",
      role: "Lead Data Scientist",
      location: "San Diego, CA",
      since_date: "May 2020 - Present",
      description:
        "Developing cutting-edge AI models and predictive analytics for business intelligence.",
      size: "80-220",
      valuation: "$90M",
      growth: "210%",
    },
    attributes: [
      { key: "location", title: "Location", value: "San Diego, California" },
      {
        key: "experience",
        title: "Work Experience",
        value: "10+ years in data science",
      },
    ],
  },
  {
    user: {
      first_name: "Jennifer",
      last_name: "Martinez",
      profile_picture_url: "/guys/woman/w-5.webp",
    },
    headline: "Chief Financial Officer",
    company: {
      name: "Copperline Studios",
      logo_url: "/companies/Copperline Studios.webp",
      role: "CFO",
      location: "Denver, CO",
      since_date: "August 2019 - Present",
      description:
        "Revolutionizing creative financial strategies for sustainable growth and innovation.",
      size: "60-180",
      valuation: "$70M",
      growth: "165%",
    },
    attributes: [
      { key: "location", title: "Location", value: "Denver, Colorado" },
      {
        key: "experience",
        title: "Work Experience",
        value: "17+ years in finance",
      },
    ],
  },
  {
    user: {
      first_name: "Robert",
      last_name: "Kim",
      profile_picture_url: "/guys/man/m-5.webp",
    },
    headline: "Head of Design",
    company: {
      name: "Orion Vale Technologies",
      logo_url: "/companies/Orion Vale Technologies.webp",
      role: "Head of Design",
      location: "Portland, OR",
      since_date: "November 2020 - Present",
      description:
        "Crafting beautiful and intuitive user experiences that delight customers worldwide.",
      size: "110-280",
      valuation: "$100M",
      growth: "180%",
    },
    attributes: [
      { key: "location", title: "Location", value: "Portland, Oregon" },
      {
        key: "experience",
        title: "Work Experience",
        value: "14+ years in design leadership",
      },
    ],
  },
];

export default function LandingPageComponent({
  data,
}: LandingPageComponentProps) {
  const headlineDesktop =
    data?.headlineDesktop ||
    "Intent Matchmaking That\nGuarantees Meaningful Connections";
  const headlineMobile =
    data?.headlineMobile ||
    "Intent Matchmaking That Guarantees Meaningful Connections";
  const description =
    data?.description ||
    "AI-powered attendee profiling, matching, and curated seating for your conference lunches. Works alongside your existing app.";
  const ctaButtonText = data?.ctaButtonText || "Get Started";
  const handleRequestDemo = useCallback(() => {
    trackCTAClicked({
      cta_location: "hero",
      cta_type: "demo",
      cta_text: ctaButtonText,
    });
    trackMeetingModalOpened("hero");
    openCalModal("momentumworld/30min");
  }, [ctaButtonText]);
  const ctaButtonLink = data?.ctaButtonLink || "/";
  const footerText = data?.footerText || "";
  const backgroundImageUrl =
    data?.backgroundImage?.url || "/branding/landing-bg.jpg";
  const heroImageUrl = data?.heroImage?.url || "/landing-stuff/hero-top.png";
  return (
    <div
      id="hero"
      className="font-host-grotesk relative w-full min-h-svh lg:min-h-180 flex flex-col items-center justify-center bg-black overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-bottom bg-no-repeat"
        style={{ backgroundImage: `url('${backgroundImageUrl}')` }}
      />

      <div className="mx-auto w-full relative z-10 flex h-full max-w-330 px-5 lg:px-16 flex-col gap-0 lg:gap-8 justify-center pt-28 lg:pt-40 xl:pt-0 pb-20 lg:pb-40 xl:pb-0">
        <div className="w-full flex flex-col gap-6 xl:flex-row items-center xl:items-center xl:justify-between">
          <div className="z-10 items-center xl:items-start xl:mt-0 w-full xl:w-[60svw] flex flex-col gap-6 max-w-200">
            {/* Main Headline */}

            <TextAnimate
              animation="blurInUp"
              by="line"
              className="hidden xl:block text-white font-host-grotesk text-4xl md:text-5xl lg:text-5xl text-left"
              as="h2"
              duration={0.2}
              once={true}
            >
              {headlineDesktop}
            </TextAnimate>

            <TextAnimate
              animation="blurInUp"
              by="line"
              className="block max-w-140 xl:hidden text-white font-host-grotesk text-4xl md:text-5xl lg:text-5xl text-center"
              as="h2"
              duration={0.2}
              once={true}
            >
              {headlineMobile}
            </TextAnimate>

            {/* Description */}
            <TextAnimate
              animation="blurInUp"
              by="line"
              className="text-white/70 max-w-120 text-center xl:text-left text-base sm:text-lg md:text-xl leading-relaxed"
              as="p"
              delay={0.2}
              duration={0.2}
              once={true}
            >
              {description}
            </TextAnimate>
            {/* CTA Action */}
            <NavbarGradientButton
              className="inline-block bg-[#D4A84B] text-black px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#E5C158] transition-colors"
              link={ctaButtonLink}
              onRequestDemo={handleRequestDemo}
            >
              <span>{ctaButtonText}</span>
            </NavbarGradientButton>
            {/* Event Details */}
            {footerText && (
              <div className="text-white/70 text-sm text-center md:text-left">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.1 }}
                >
                  <p>{footerText}</p>
                </motion.div>
              </div>
            )}
          </div>
          {/* <Image
            src={heroImageUrl}
            alt="Hero background"
            className="object-contain object-center-center h-[35rem] w-auto mr-[-5rem] xl:mr-[-10rem]"
            width={1324}
            height={1015}
            priority
            style={{
              objectFit: "contain",
              objectPosition: "center center",
            }}
          /> */}
          <ProfilePersonaContainer profiles={sampleProfiles} />
        </div>
      </div>
    </div>
  );
}
